// src/pages/CourseTime/components/slides/assessment/OrderingQuestion.comp.jsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { GET_APP_LOCALIZE_TEXT } from '../../../../../utils/dq_lc_service/lc_service';

// Styled components
const MTQHint = styled.div`
  width: 100%;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const MTQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const MTQItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1.5px solid #e3e5e8;
  background-color: #fff;
  user-select: none;
  cursor: ${props => props.dragging ? 'grabbing' : 'grab'};
  touch-action: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: ${props => props.borderColor || '#fe696e'};
  }

  ${props => props.dragging && `
    position: relative;
    z-index: 2;
    border-color: ${props.borderColor || '#fe696e'};
    background-color: ${props.borderColor || '#fe696e'}14;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  `}

  ${props => !props.dragging && props.displaced && `
    background-color: ${props.borderColor || '#fe696e'}14;
  `}
`;

const MTQDragHandle = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 3px;
  place-items: center;

  span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #b9bdc4;
  }
`;

const MTQItemText = styled.div`
  color: #2b2f36;
  font-weight: 600;
  font-size: 16px;
  text-align: left;
`;

// MTQ (drag-to-sort ordering question).
const OrderingQuestion = ({
  processedChoices,
  slideId,
  currentAnswer,
  setAnswersMap,
  themeColor,
  renderSpokenText,
  pageLc
}) => {
  // `mtqOrder` holds the current display order as an array of original option indices.
  const [mtqOrder, setMtqOrder] = useState([]);
  const [mtqDraggingIdx, setMtqDraggingIdx] = useState(null);
  const [mtqDragOffsetY, setMtqDragOffsetY] = useState(0);
  const [mtqDisplacedIdx, setMtqDisplacedIdx] = useState(null);
  const mtqItemRefs = useRef({});
  const mtqDragMetaRef = useRef(null);
  // Mirror of mtqOrder so pointermove can reorder synchronously — doing the
  // reorder inside the setMtqOrder updater ran side effects at render time
  // (twice under StrictMode), which double-shifted the drag anchor and jittered.
  const mtqOrderRef = useRef([]);

  // Reset/restore the sort order whenever the slide (or its choices) change.
  useEffect(() => {
    const restored = Array.isArray(currentAnswer) && currentAnswer.length === processedChoices.length
      ? currentAnswer
      : processedChoices.map((_, idx) => idx);

    mtqOrderRef.current = restored;
    setMtqOrder(restored);

    // Commit the initial order as the answer so the default arrangement is a valid submission.
    if (!Array.isArray(currentAnswer) || currentAnswer.length !== processedChoices.length) {
      setAnswersMap(prev => ({ ...prev, [slideId]: restored }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideId, processedChoices.length]);

  // Dragging is driven off window-level pointer listeners (not element-level
  // onPointerMove/Up with setPointerCapture) so the gesture keeps tracking even
  // though the drag handle itself is a small target - capture support/behavior
  // is inconsistent enough across browsers that relying on it dropped moves.
  const handleMtqPointerDown = (event, optionIdx) => {
    event.preventDefault();

    const itemEl = mtqItemRefs.current[optionIdx];
    const itemHeight = (itemEl ? itemEl.offsetHeight : 60) + 12; // row height + gap

    mtqDragMetaRef.current = { startClientY: event.clientY, itemHeight };
    setMtqDraggingIdx(optionIdx);
    setMtqDragOffsetY(0);
  };

  useEffect(() => {
    if (mtqDraggingIdx === null) return undefined;

    const handleMove = (event) => {
      const meta = mtqDragMetaRef.current;
      if (!meta) return;
      event.preventDefault();

      const rawOffset = event.clientY - meta.startClientY;
      const steps = Math.round(rawOffset / meta.itemHeight);

      if (steps !== 0) {
        const prevOrder = mtqOrderRef.current;
        const currentPos = prevOrder.indexOf(mtqDraggingIdx);
        if (currentPos !== -1) {
          const targetPos = Math.min(Math.max(currentPos + steps, 0), prevOrder.length - 1);
          if (targetPos !== currentPos) {
            const displacedItemIdx = prevOrder[targetPos];
            const newOrder = [...prevOrder];
            newOrder.splice(currentPos, 1);
            newOrder.splice(targetPos, 0, mtqDraggingIdx);
            // Shift the anchor by the rows actually moved (targetPos may be
            // clamped at the list edges — raw `steps` would drift the anchor).
            meta.startClientY += (targetPos - currentPos) * meta.itemHeight;
            mtqOrderRef.current = newOrder;
            setMtqOrder(newOrder);
            setAnswersMap(prev => ({ ...prev, [slideId]: newOrder }));
            setMtqDisplacedIdx(displacedItemIdx);
          }
        }
      }

      setMtqDragOffsetY(event.clientY - meta.startClientY);
    };

    const handleUp = () => {
      setMtqDraggingIdx(null);
      setMtqDragOffsetY(0);
      setMtqDisplacedIdx(null);
      mtqDragMetaRef.current = null;
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, [mtqDraggingIdx, slideId, setAnswersMap]);

  return (
    <>
      <MTQHint>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__drag_and_drop_to_order')}</MTQHint>
      <MTQList>
        {mtqOrder.map((optionIdx) => {
          const option = processedChoices[optionIdx];
          const dragging = mtqDraggingIdx === optionIdx;
          const displaced = mtqDisplacedIdx === optionIdx;

          return (
            <MTQItem
              key={optionIdx}
              ref={(el) => { if (el) mtqItemRefs.current[optionIdx] = el; }}
              dragging={dragging}
              displaced={displaced}
              borderColor={themeColor}
              style={dragging ? { transform: `translateY(${mtqDragOffsetY}px)` } : undefined}
              onPointerDown={(e) => handleMtqPointerDown(e, optionIdx)}
            >
              <MTQDragHandle dragging={dragging}>
                <span /><span /><span /><span /><span /><span />
              </MTQDragHandle>
              <MTQItemText>
                <span>{renderSpokenText ? renderSpokenText(`option_${optionIdx}`, option) : option}</span>
              </MTQItemText>
            </MTQItem>
          );
        })}
      </MTQList>
    </>
  );
};

export default OrderingQuestion;
